import React, { ReactNode } from "react";
import {
  Routes,
  Route,
  Navigate,
  NavigateFunction,
  Params,
} from "react-router-dom";
import { Subject, takeUntil } from "rxjs";
import { Provider, inject, observer } from "mobx-react";
import DonationProtectedRoutes from "./DonationProtectedRoutes";
import PricingPage from "./PricingPage";
import DonationOptionsPage from "./DonationOptionsPage";
import { CategoryService, DonationService } from "../../../shared/services";
import {
  AuthService,
  FlexContainer,
  Loader,
  RedButton,
  SapphireBlueButton,
  ToastService,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import { MediaTypes, UpsertDonationActiveStep } from "../../../shared/enums";
import { DonationStore, RolesAndPermissionStore } from "../../../shared/store";
import {
  Header,
  ItemDetailPage,
  ItemHeader,
  Layout,
  MediaBoxes,
  SideBar,
} from "../../../shared/components";
import { ProductDetailWrapper } from "../../../shared/styles";
import {
  CountryWithStateModel,
  ItemDetailInputModel,
  MediaModel,
} from "../../../shared/models";
import { toJS } from "mobx";
import FullLibraryView from "../../../shared/components/FullLibraryView";
import { getMerchantAccountStore } from "../../merchantAccounts/MerchantAccountStore";

interface IProps {
  donationStore?: DonationStore;
  navigate: NavigateFunction;
  params: Readonly<Params<string>>;
  rolesAndPermissionStore: RolesAndPermissionStore;
  countryDataLoaded: boolean;
  countriesWithState: CountryWithStateModel[];
}

type IState = {
  showLibraryView: boolean;
  showUpload?: boolean;
};

export const getCategoryService = (() => {
  let instance: CategoryService | null = null;
  return () => {
    if (!instance) {
      instance = new CategoryService();
    }
    return instance;
  };
})();

@inject("donationStore")
@observer
class DonationRoutes extends React.Component<IProps, IState> {
  private loader: Loader = new Loader(false);
  private $destroy: Subject<boolean> = new Subject<boolean>();
  private disposeSurchargeDefault?: () => void;

  constructor(props: IProps) {
    super(props);
    this.state = {
      showLibraryView: false,
      showUpload: false,
    };
  }

  toggleLibraryView = (show: boolean, fromUpload: boolean = false) => {
    this.setState(() => ({
      showLibraryView: show,
      showUpload: fromUpload,
    }));
  };

  componentDidMount(): void {
    this.props.donationStore?.reset();
    if (this.donationId) {
      this.loader.show();

      DonationService.getDonation(
        AuthService.authData.accountId,
        this.donationId,
      )
        .pipe(takeUntil(this.$destroy))
        .subscribe({
          next: (result) => {
            if (!result.result) {
              ToastService.showError("Error in loading product details");
            } else {
              this.loader.hide();
              this.props.donationStore?.constructStoreFromAPI(result.result);
            }
          },
          error: () => {
            ToastService.showError("Error in loading product details");
          },
        });
    } else {
      this.setDefaultSurcharge();
    }
  }

  /* A new donation picks up the surcharge setting of the merchant account it
     will be collected through. Only runs when creating: an existing donation
     keeps the setting it was saved with. */
  setDefaultSurcharge = () => {
    this.disposeSurchargeDefault =
      getMerchantAccountStore().whenItemSurchargeDefaultKnown((surcharge) => {
        const { donationStore } = this.props;
        if (!donationStore) return;
        const updated = ItemDetailInputModel.withMerchantSurchargeDefault(
          donationStore.detailPage,
          surcharge,
        );
        if (updated) donationStore.updateDetailsPage(updated);
      });
  };

  onCancel() {
    if (
      this.props.donationStore?.activeStep == UpsertDonationActiveStep.Pricing
    ) {
      this.onBackOrNext(false);
      return;
    }
    this.props.navigate("/app/items");
  }

  get donationId() {
    const split = ((this.props.params && this.props.params["*"]) || "").split(
      "/",
    );
    const id = split[split.length - 1];
    if (UtilityMethods.isValidObjectId(id)) {
      return id;
    }
    return null;
  }

  componentWillUnmount(): void {
    this.$destroy.next(true);
    this.disposeSurchargeDefault?.();
  }

  onBackOrNext(isNext: boolean) {
    if (this.loader.showLoader) return;
    let queryParam: string = "";
    if (this.donationId) {
      queryParam = "/" + this.donationId;
    }
    if (isNext) {
      const success = this.props.donationStore?.validate();
      if (!success) return;
    }
    switch (this.props.donationStore?.activeStep) {
      case UpsertDonationActiveStep.Detail:
        this.props.donationStore?.updateActiveStep(
          UpsertDonationActiveStep.Pricing,
        );
        this.props.navigate(`/app/donation/pricing${queryParam}`);
        break;
      case UpsertDonationActiveStep.Pricing:
        isNext
          ? this.props.donationStore?.updateActiveStep(
              UpsertDonationActiveStep.Options,
            )
          : this.props.donationStore?.updateActiveStep(
              UpsertDonationActiveStep.Detail,
            );
        isNext
          ? this.props.navigate(`/app/donation/options${queryParam}`)
          : this.props.navigate(`/app/donation/detail${queryParam}`);
        break;

      case UpsertDonationActiveStep.Options:
        this.props.donationStore?.updateActiveStep(
          UpsertDonationActiveStep.Pricing,
        );
        this.props.navigate(`/app/donation/pricing${queryParam}`);

        break;
    }
  }

  stepConfig = {
    [UpsertDonationActiveStep.Detail]: {
      name: "detail",
      url: "/app/donation/detail",
    },
    [UpsertDonationActiveStep.Pricing]: {
      name: "pricing",
      url: "/app/donation/pricing",
    },
    [UpsertDonationActiveStep.Options]: {
      name: "options",
      url: "/app/donation/options",
    },
  };

  onTabChange = (key: UpsertDonationActiveStep) => {
    const queryParam = this.donationId ? `/${this.donationId}` : "";

    const validationResult =
      this.props.donationStore?.validatePreviousSteps(key);
    if (!validationResult) {
      ToastService.showError("Unable to validate steps.");
      return;
    }

    const { isValid, errorStep } = validationResult;

    if (!isValid && errorStep) {
      ToastService.showError(
        `Please fill all the required fields in donation ${this.stepConfig[errorStep].name} before proceeding.`,
      );
      this.props.donationStore?.updateActiveStep(errorStep);
      this.props.navigate(`${this.stepConfig[errorStep].url}${queryParam}`);
      return;
    }

    this.props.donationStore?.updateActiveStep(key);
    this.props.navigate(`${this.stepConfig[key].url}${queryParam}`);
  };

  onSave = (isSaveAndExit: boolean = false) => {
    if (this.loader.showLoader) return;
    if (!this.props.donationStore) return;

    if (isSaveAndExit) {
      const validationResult = this.props.donationStore?.validatePreviousSteps(
        UpsertDonationActiveStep.Options,
      );
      if (!validationResult) {
        ToastService.showError("Validation failed.");
        return;
      }

      const { isValid, errorStep } = validationResult;

      if (!isValid && errorStep) {
        ToastService.showError(
          `Please fill all the required fields in donation ${this.stepConfig[errorStep].name} before saving.`,
        );
        this.props.donationStore?.updateActiveStep(errorStep);
        this.props.navigate(
          `${this.stepConfig[errorStep].url}/${this.donationId || ""}`,
        );
        return;
      }
      const isOptionsValid = this.props.donationStore?.validate(
        UpsertDonationActiveStep.Options,
      );
      if (!isOptionsValid) {
        ToastService.showError(
          "Please fill all required fields in the Options step before saving.",
        );
        this.props.donationStore?.updateActiveStep(
          UpsertDonationActiveStep.Options,
        );
        this.props.navigate(
          `${this.stepConfig[UpsertDonationActiveStep.Options].url}/${this.donationId || ""}`,
        );
        return;
      }
    } else {
      const success = this.props.donationStore?.validate();
      if (!success) return;
    }
    const request = this.props.donationStore.createAPIModel();
    if (this.props.donationStore?.isSaveUpdateInProgress) return;
    this.props.donationStore?.toggleSaveUpdateInProgress();
    if (this.donationId) {
      request.id = this.donationId;
      DonationService.update(request).subscribe({
        next: (response) => {
          if (response) {
            ToastService.showSuccess("Donation updated Successfully");
            this.props.donationStore?.toggleSaveUpdateInProgress();
            if (isSaveAndExit) {
              this.props.navigate("/app/items");
            }
          }
        },
        error: () => {
          ToastService.showError("Donation update failed!");
          this.props.donationStore?.toggleSaveUpdateInProgress();
        },
      });
    } else {
      DonationService.save(request).subscribe({
        next: (response) => {
          if (response.id) {
            ToastService.showSuccess("Saved Successfully");
            this.props.donationStore?.toggleSaveUpdateInProgress();
            if (isSaveAndExit) {
              this.props.navigate("/app/items");
            }
          }
        },
        error: () => {
          ToastService.showError("Error in saving");
          this.props.donationStore?.toggleSaveUpdateInProgress();
        },
      });
    }
  };

  get showNextButton(): boolean {
    return (
      this.props.donationStore?.activeStep !== UpsertDonationActiveStep.Options
    );
  }

  get showBackbutton(): boolean {
    return (
      this.props.donationStore?.activeStep !== UpsertDonationActiveStep.Detail
    );
  }

  get headerCancelButtonCaption(): string {
    return this.props.donationStore?.activeStep ==
      UpsertDonationActiveStep.Pricing
      ? "Back"
      : "";
  }

  onSelectLibraryMedia(media: MediaModel) {
    this.props.donationStore?.updateDetailsPage(
      new ItemDetailInputModel({
        ...this.props.donationStore?.detailPage,
        medias: [...this.props.donationStore?.detailPage.medias, media],
        showLibrary: false,
      }),
    );
  }

  deleteMedia(medias: MediaModel[]) {
    this.props.donationStore?.updateDetailsPage(
      new ItemDetailInputModel({
        ...this.props.donationStore?.detailPage,
        medias,
      }),
    );
  }

  onFavourite(media: MediaModel) {
    const updatedMedia = (
      toJS(this.props.donationStore?.detailPage?.medias) || []
    ).map((x) => {
      if (x.mediaId === media.mediaId) {
        return { ...x, isFavourite: true } as MediaModel;
      } else if (x.isFavourite) {
        return { ...x, isFavourite: false } as MediaModel;
      }
      return x;
    });
    const index = updatedMedia.findIndex((x) => x.mediaId === media.mediaId);
    if (index !== -1) {
      const [favouritedMedia] = updatedMedia.splice(index, 1);
      updatedMedia.unshift(favouritedMedia);
    }
    this.props.donationStore?.updateDetailsPage(
      new ItemDetailInputModel({
        ...this.props.donationStore?.detailPage,
        medias: [...updatedMedia],
      }),
    );
  }

  onDelete(media: MediaModel) {
    const updatedMedia = (
      toJS(this.props.donationStore?.detailPage?.medias) || []
    ).map((x) => x);
    const index = updatedMedia.findIndex((x) => x.mediaId === media.mediaId);
    updatedMedia.splice(index, 1);
    this.deleteMedia(updatedMedia);
  }

  updateMedia(medias: MediaModel[]) {
    this.props.donationStore?.updateDetailsPage(
      new ItemDetailInputModel({
        ...this.props.donationStore?.detailPage,
        medias: [...this.props.donationStore?.detailPage.medias, ...medias],
      }),
    );
  }

  get footerButtons() {
    return (
      <FlexContainer
        alignItems="center"
        justifyContent="center"
        spacing={15}
        size={0}
        padding="10px 0px 0px"
        width="calc(100% - 480px)"
      >
        <RedButton height="40px" onClick={() => this.onCancel()}>
          Cancel
        </RedButton>
        {this.showBackbutton && (
          <SapphireBlueButton
            height="40px"
            onClick={() => this.onBackOrNext(false)}
          >
            Back
          </SapphireBlueButton>
        )}

        {this.showNextButton && (
          <SapphireBlueButton
            height="40px"
            onClick={() => this.onBackOrNext(true)}
          >
            Next
          </SapphireBlueButton>
        )}
      </FlexContainer>
    );
  }
  get routes() {
    return (
      <Routes>
        <Route
          path={`/detail/:id?`}
          element={
            <DonationProtectedRoutes currentRoute="detail">
              <>
                <ProductDetailWrapper>
                  <ItemHeader header="Donation Details" />
                  <ItemDetailPage
                    isProduct={false}
                    isDonation={true}
                    itemDetail={this.props.donationStore?.detailPage}
                    onUpdate={(detail: ItemDetailInputModel) =>
                      this.props.donationStore?.updateDetailsPage(detail)
                    }
                    toggleLibrary={(show: boolean, fromUpload: boolean) =>
                      this.toggleLibraryView(show, fromUpload)
                    }
                    showLibraryView={this.state.showLibraryView}
                  />
                </ProductDetailWrapper>
              </>
            </DonationProtectedRoutes>
          }
        />
        <Route
          path={`/pricing/:id?`}
          element={
            <DonationProtectedRoutes currentRoute="pricing">
              <>
                <PricingPage />
              </>
            </DonationProtectedRoutes>
          }
        />

        <Route
          path={`/options/:id?`}
          element={
            <DonationProtectedRoutes currentRoute="options">
              <>
                <DonationOptionsPage
                  donationId={this.donationId}
                  countriesWithState={this.props.countriesWithState}
                />
              </>
            </DonationProtectedRoutes>
          }
        />
        <Route path="*" element={<Navigate to="/app/donation/detail" />} />
      </Routes>
    );
  }

  render(): ReactNode {
    if (!this.props.countryDataLoaded) {
      return <>{new Loader().loaderComponent}</>;
    }
    if (this.state.showLibraryView) {
      return (
        <FullLibraryView
          multiple={true}
          medias={toJS(this.props.donationStore?.detailPage.medias || [])}
          onUpload={(medias: MediaModel[]) => this.updateMedia(medias)}
          onDelete={(media: MediaModel) => this.onDelete(media)}
          onSelectLibraryMedia={(media) =>
            this.onSelectLibraryMedia(MediaModel.mediaItemToMediaModel(media))
          }
          onFavourite={(media: MediaModel) => this.onFavourite(media)}
          allowedMediaTypes={[MediaTypes.Image, MediaTypes.Video]}
          toggleLibrary={(show) => this.toggleLibraryView(show)}
          showLibrary={this.state.showLibraryView}
          updateMedia={(medias: MediaModel[]) => this.updateMedia(medias)}
          showUpload={this.state.showUpload}
        />
      );
    }
    return (
      <Provider categoryService={getCategoryService()}>
        <Layout
          sideBarComponent={
            <SideBar
              activeStep={
                this.props.donationStore?.activeStep?.toString() || ""
              }
              showVariant={false}
              onTabChange={(key: UpsertDonationActiveStep) =>
                this.onTabChange(key)
              }
            />
          }
          headerCaption={""}
          cancelClick={() => this.onCancel()}
          nextButtonCaption={this.showNextButton ? "Next" : ""}
          nextClick={() => this.onBackOrNext(true)}
          showBackButton={this.showBackbutton}
          showNextButton={this.showNextButton}
          backClick={() => this.onBackOrNext(false)}
          saveAndExitClick={() => this.onSave(true)}
          showSaveAndExit={this.donationId ? true : false}
          disableSaveAndExit={
            this.loader.showLoader ||
            !!this.props.donationStore?.isSaveUpdateInProgress
          }
          showCancelButton={false}
          headerComponent={
            <Header
              headerCaption={"CREATE YOUR ITEM"}
              showSaveButton={!this.showNextButton}
              disableSaveButton={!this.donationId}
              showSaveAndExitButton={true}
              disableSaveAndExitButton={
                this.loader.showLoader ||
                !!this.props.donationStore?.isSaveUpdateInProgress
              }
              saveAndExitButtonCaption={
                this.showNextButton ? "NEXT" : "SAVE & EXIT"
              }
              saveClick={() => this.onSave()}
              cancelClick={() => this.onCancel()}
              cancelButtonCaption={this.headerCancelButtonCaption}
              saveAndExitClick={() =>
                this.showNextButton
                  ? this.onBackOrNext(this.showNextButton)
                  : this.onSave(true)
              }
            />
          }
          showHeader={false}
          showFooter={!this.loader.showLoader}
        >
          {this.loader.showLoader ? this.loader.loaderComponent : this.routes}
        </Layout>
      </Provider>
    );
  }
}

export default withRouter(DonationRoutes);
